// GuestRoomBooking — Phase I follow-up: public-facing room booking widget.
// Reached at /guest/book/:orgId — operator embeds this URL on their own site.
// Calls /api/public/rooms/availability?organization_id=...&date=... and
// /api/public/rooms/book (both NO AUTH).
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Calendar, MapPin, Loader2, CheckCircle2, Users, Clock, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const todayIso = () => new Date().toISOString().slice(0, 10);

const GuestRoomBooking = () => {
  const { orgId } = useParams();
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [room, setRoom] = useState(null);
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [party, setParty] = useState(2);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true); setError(null);
    fetch(`${API_URL}/api/public/rooms/availability?organization_id=${encodeURIComponent(orgId)}&date=${date}`)
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.detail || r.statusText)))
      .then(setData)
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [orgId, date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !time) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/rooms/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: orgId,
          room_id: room?.id || null,
          guest_name: name,
          guest_email: email || null,
          guest_phone: phone || null,
          party_size: party,
          date, time, notes: notes || null,
        }),
      });
      if (res.ok) setDone(await res.json());
      else setDone({ error: (await res.json().catch(() => ({}))).detail || res.statusText });
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-surface-muted grid place-items-center" data-testid="guest-room-loading"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-surface-muted grid place-items-center px-4" data-testid="guest-room-error">
        <div className="bg-surface border border-surface-border rounded-lg p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-warn mb-3" />
          <h1 className="font-display text-xl text-ink">Booking unavailable</h1>
          <p className="text-sm text-ink-muted mt-2">{error || 'We couldn’t load this venue’s rooms.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="guest-room-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-xs uppercase tracking-overline text-accent-500 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" /> Reserve a table
        </div>
        <h1 className="font-display text-3xl text-ink mt-1">{data.organization?.name || 'Book a table'}</h1>

        <div className="bg-surface border border-surface-border rounded-lg p-5 mt-6">
          <label className="text-xs uppercase tracking-overline text-ink-muted">Date</label>
          <input aria-label="Date"
            type="date" min={todayIso()} value={date} onChange={(e) => { setDate(e.target.value); setRoom(null); setTime(''); setDone(null); }}
            className="mt-1 block px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm font-data"
            data-testid="guest-room-date"
          />
        </div>

        <div className="space-y-4 mt-6" data-testid="guest-room-rooms">
          {(data.rooms || []).length === 0 ? (
            <p className="text-sm text-ink-muted italic px-1">No rooms configured for this venue yet.</p>
          ) : (
            data.rooms.map(r => (
              <article key={r.id} className={`bg-surface rounded-lg border p-4 ${room?.id === r.id ? 'border-primary-500' : 'border-surface-border'}`} data-testid={`guest-room-card-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-display text-lg text-ink">{r.name}</h2>
                    {r.description ? <p className="text-sm text-ink-muted mt-0.5">{r.description}</p> : null}
                  </div>
                  {r.capacity ? <span className="text-xs text-ink-muted font-data tabular-nums flex items-center gap-1"><Users className="w-3 h-3" />{r.capacity}</span> : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(r.slots || []).map(s => (
                    <button
                      key={s.time} disabled={!s.available}
                      onClick={() => { setRoom(r); setTime(s.time); setDone(null); }}
                      className={`text-xs px-3 py-1.5 rounded-md border motion-organic ${
                        room?.id === r.id && time === s.time
                          ? 'bg-primary-500 text-surface border-primary-500'
                          : s.available
                            ? 'bg-surface border-surface-border text-ink hover:bg-primary-50'
                            : 'bg-surface-sunken border-surface-border text-ink-subtle cursor-not-allowed line-through'
                      }`}
                      data-testid={`guest-room-slot-${r.id}-${s.time}`}
                    >
                      <Clock className="w-3 h-3 inline mr-1" />{s.time}
                    </button>
                  ))}
                </div>
              </article>
            ))
          )}
        </div>

        {room && time ? (
          done && !done.error ? (
            <div className="mt-8 bg-ok/5 border border-ok/30 rounded-lg p-5 text-center" data-testid="guest-room-success">
              <CheckCircle2 className="w-8 h-8 mx-auto text-ok mb-2" />
              <p className="font-display text-lg text-ink">{done.message || 'Booking received.'}</p>
              <p className="text-sm text-ink-muted mt-1 font-data tabular-nums">{room.name} · {new Date(date).toLocaleDateString()} · {time}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 bg-surface border border-surface-border rounded-lg p-5 space-y-4" data-testid="guest-room-form">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-ink-muted" />
                <span className="font-medium text-ink">{room.name}</span>
                <span className="text-ink-muted">·</span>
                <span className="font-data tabular-nums">{new Date(date).toLocaleDateString()} {time}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field testid="guest-room-name" label="Your name" required value={name} onChange={setName} />
                <Field testid="guest-room-email" label="Email" type="email" value={email} onChange={setEmail} />
                <Field testid="guest-room-phone" label="Phone" value={phone} onChange={setPhone} />
                <div>
                  <label className="text-xs uppercase tracking-overline text-ink-muted">Party size</label>
                  <input aria-label="Party size" type="number" min="1" max="20" value={party} onChange={(e) => setParty(parseInt(e.target.value || '1', 10))}
                    className="mt-1 w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm font-data tabular-nums"
                    data-testid="guest-room-party" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-overline text-ink-muted">Notes (allergies, occasion)</label>
                <textarea aria-label="Notes (allergies, occasion)" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm"
                  data-testid="guest-room-notes" />
              </div>
              <button
                type="submit" disabled={!name || submitting}
                className="w-full px-4 py-3 bg-accent-500 text-ink rounded-md text-sm font-semibold hover:bg-accent-600 disabled:opacity-40 motion-organic"
                data-testid="guest-room-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> : null}
                Request booking
              </button>
              {done?.error ? <p className="text-xs text-danger" data-testid="guest-room-error-banner">{done.error}</p> : null}
            </form>
          )
        ) : null}

        <p className="text-xs text-ink-muted text-center mt-10">Powered by Smart F&amp;B·OS</p>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', required, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-overline text-ink-muted">{label}{required ? ' *' : ''}</label>
    <input
      type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm"
      data-testid={testid}
    />
  </div>
);

export default GuestRoomBooking;
